// CRM Pipeline — Potansiyel cariler için Kanban görünümü.
// Aynı prospect_accounts verisini (status bazlı) kanban kartlar olarak gösterir.
// Drag-drop ile status değiştirir; "Kazanıldı" kolonuna sürüklemek = otomatik /convert çağrısı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import KanbanBoard from '../components/KanbanBoard';
import { toast } from '../components/Toast';
import RecordChatter from '../components/RecordChatter';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const COLUMNS = [
  { key: 'open',        label: 'Açık',              accent: '#64748b' },
  { key: 'contacted',   label: 'İletişim Kuruldu',  accent: '#3b82f6' },
  { key: 'negotiation', label: 'Görüşmede',         accent: '#f59e0b' },
  { key: 'converted',   label: 'Kazanıldı',         accent: '#10b981' },
  { key: 'lost',        label: 'Kaybedildi',        accent: '#ef4444' },
];

const TYPE_LABELS = {
  customer: 'Müşteri', supplier: 'Tedarikçi', both: 'M&T',
  retail_customer: 'Perakende', dealer: 'Bayi', ecommerce: 'E-Ticaret', other: 'Diğer',
};

const SOURCE_ICONS = {
  web: '🌐', referans: '🤝', referral: '🤝', fuar: '🎪', soguk: '❄', cold: '❄',
  whatsapp: '💬', instagram: '📷', facebook: '📘', meta: '📘', google: '🔎', telefon: '📞',
};

function pickSourceIcon(source) {
  if (!source) return '👤';
  const s = source.toLowerCase();
  for (const k of Object.keys(SOURCE_ICONS)) {
    if (s.includes(k)) return SOURCE_ICONS[k];
  }
  return '👤';
}

export default function CrmPipelinePage({ onNavigate }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  // Lost Reason modal state — kart "Kaybedildi"ye sürüklenince açılır
  const [lostModal, setLostModal] = useState(null); // { prospectId, prospectTitle, prevStatus }
  const [lostReasons, setLostReasons] = useState([]);
  const [lostForm, setLostForm] = useState({ lost_reason_id: '', lost_note: '' });

  // Lost reasons listesini önceden yükle
  useEffect(() => {
    api.get('/crm/lost-reasons').then(r => {
      if (r.data?.success) setLostReasons(r.data.data || []);
    }).catch(() => {});
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      // Kazanılan ve kaybedilenler de pipeline'da görünsün
      const r = await api.get('/prospect-accounts', { params: { search, include_converted: 'yes', limit: 500 } });
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Kanban kartları: her prospect bir kart, columnKey = status
  const cards = list.map(p => ({
    id: p.id,
    columnKey: p.status || 'open',
    raw: p,
  }));

  // Kart toplamları (kolonbaşı için ileride genişletilebilir — bu MVP sadece sayı gösteriyor)

  const handleMove = async (cardId, fromCol, toCol, card) => {
    const p = card.raw;
    try {
      // Kazanıldı kolonuna sürüklendiğinde: otomatik cariye aktar
      if (toCol === 'converted' && !p.converted_to_account_id) {
        const r = await api.post('/prospect-accounts/' + p.id + '/convert');
        if (r.data.success) {
          await fetchList();
          toast.success(`${p.title} cariye aktarıldı: ${r.data.data.code}`);
          return;
        }
        throw new Error(r.data.message || 'Aktarım başarısız');
      }
      // Kaybedildi kolonuna sürüklendiğinde: önce neden sor (modal aç + throw → kart eski yerinde kalır)
      if (toCol === 'lost' && fromCol !== 'lost') {
        setLostModal({ prospectId: p.id, prospectTitle: p.title, prevStatus: fromCol });
        setLostForm({ lost_reason_id: '', lost_note: '' });
        // throw → KanbanBoard rollback yapar, modal kapanırsa kart eski yerinde kalır
        // eslint-disable-next-line no-throw-literal
        throw { silent: true };
      }
      // Diğer durumlar: sadece status update (mevcut PUT endpoint'ini kullan)
      const payload = { ...p, status: toCol };
      const r = await api.put('/prospect-accounts/' + p.id, payload);
      if (!r.data.success) throw new Error(r.data.message || 'Güncellenemedi');
      setList(prev => prev.map(x => x.id === p.id ? { ...x, status: toCol } : x));
      const colLabel = COLUMNS.find(c => c.key === toCol)?.label || toCol;
      toast.success(`${p.title} → ${colLabel}`);
    } catch (e) {
      if (!e?.silent) toast.error(e.response?.data?.message || e.message || 'Hata');
      throw e; // KanbanBoard onMove throw'da kendi rollback yapıyor
    }
  };

  const confirmLost = async () => {
    if (!lostModal) return;
    try {
      await api.post('/crm/prospects/' + lostModal.prospectId + '/mark-lost', {
        lost_reason_id: lostForm.lost_reason_id || null,
        lost_note: lostForm.lost_note || null,
      });
      // UI: kartı 'lost'a taşı
      setList(prev => prev.map(x => x.id === lostModal.prospectId ? { ...x, status: 'lost' } : x));
      toast.success(`${lostModal.prospectTitle} → Kaybedildi`);
      setLostModal(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Hata');
    }
  };

  const handleCardClick = async (card) => {
    try {
      const r = await api.get('/prospect-accounts/' + card.id);
      if (r.data.success) {
        setSelected(r.data.data);
        setForm({ ...r.data.data });
        setIsEditing(false);
        setModalTab('info');
        setShowModal(true);
      }
    } catch (e) { toast.error('Kayıt yüklenemedi'); }
  };

  const handleNew = () => {
    setSelected(null);
    setForm({
      title: '', type: 'customer', identity_type: 'corporate',
      phone: '', mobile: '', email: '', contact_person: '',
      city: '', source: '', status: 'open',
      currency_code: 'TRY', notes: '',
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title?.trim()) { toast.error('Ünvan zorunlu'); return; }
    setSaving(true);
    try {
      if (selected) {
        await api.put('/prospect-accounts/' + selected.id, form);
      } else {
        await api.post('/prospect-accounts', form);
      }
      await fetchList();
      toast.success('Kaydedildi');
      setShowModal(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Kayıt başarısız');
    } finally { setSaving(false); }
  };

  const handleConvert = async () => {
    if (!selected || selected.converted_to_account_id) return;
    if (!window.confirm(`"${selected.title}" cariye aktarılsın mı?`)) return;
    setConverting(true);
    try {
      const r = await api.post('/prospect-accounts/' + selected.id + '/convert');
      if (r.data.success) {
        toast.success('Cariye aktarıldı: ' + r.data.data.code);
        await fetchList();
        setShowModal(false);
      }
    } catch (e) { toast.error(e.response?.data?.message || 'Aktarım başarısız'); }
    finally { setConverting(false); }
  };

  const renderCard = (card) => {
    const p = card.raw;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: '0.95rem' }}>{pickSourceIcon(p.source)}</span>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.title}
          </span>
        </div>
        {p.contact_person && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 2 }}>
            👤 {p.contact_person}
          </div>
        )}
        {(p.phone || p.mobile) && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 2 }}>
            📞 {p.phone || p.mobile}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {p.city && (
            <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
              📍 {p.city}
            </span>
          )}
          {p.type && p.type !== 'customer' && (
            <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontWeight: 600 }}>
              {TYPE_LABELS[p.type] || p.type}
            </span>
          )}
          {p.source && (
            <span style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginLeft: 'auto' }}>
              {p.source}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ListPageHeader
        title="CRM Pipeline"
        unit="potansiyel"
        count={list.length}
        searchValue={search}
        onSearch={setSearch}
        searchPlaceholder="Ünvan, kişi, telefon ile ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={
          <>
            <Button size="sm" variant="ghost" onClick={() => onNavigate && onNavigate('prospect-accounts')}>📋 Liste Görünümü</Button>
            <Button size="sm" onClick={handleNew}>+ Yeni Potansiyel</Button>
          </>
        }
      />

      {!loading && list.length === 0 ? (
        <EmptyState
          icon="🎯"
          title="Henüz potansiyel cari yok"
          description='Yeni bir potansiyel müşteri eklemek için "+ Yeni Potansiyel" tıklayın.'
        />
      ) : (
        <KanbanBoard
          columns={COLUMNS}
          cards={cards}
          renderCard={renderCard}
          onMove={handleMove}
          onCardClick={handleCardClick}
          loading={loading}
          emptyText="Bu aşamada kayıt yok"
        />
      )}

      {showModal && (
        <Modal
          title={selected ? `Potansiyel: ${selected.title}` : 'Yeni Potansiyel Cari'}
          size="md"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {selected && !selected.converted_to_account_id && (
                  <Button variant="ghost" onClick={() => { setShowModal(false); onNavigate && onNavigate('prospect-accounts'); }}>
                    📋 Tam Kart
                  </Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                {selected && !selected.converted_to_account_id && (
                  <Button variant="success" onClick={handleConvert} loading={converting}>→ Cariye Aktar</Button>
                )}
                {isEditing ? (
                  <>
                    <Button variant="ghost" onClick={() => { if (!selected) setShowModal(false); else setIsEditing(false); }}>İptal</Button>
                    <Button onClick={handleSave} loading={saving}>💾 Kaydet</Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" onClick={() => setShowModal(false)}>Kapat</Button>
                    {selected && !selected.converted_to_account_id && (
                      <Button onClick={() => setIsEditing(true)}>✎ Düzenle</Button>
                    )}
                  </>
                )}
              </div>
            </>
          }
        >
          {selected && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid var(--border-secondary)' }}>
              {[
                { key: 'info', label: 'Bilgiler' },
                { key: 'activity', label: 'Aktivite' },
              ].map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setModalTab(t.key)}
                  style={{
                    padding: '8px 14px',
                    border: 'none',
                    background: 'transparent',
                    color: modalTab === t.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontWeight: modalTab === t.key ? 700 : 500,
                    fontSize: 'var(--text-sm)',
                    cursor: 'pointer',
                    borderBottom: '2px solid ' + (modalTab === t.key ? 'var(--accent-primary)' : 'transparent'),
                    marginBottom: -1,
                  }}
                >{t.label}</button>
              ))}
            </div>
          )}

          {(!selected || modalTab === 'info') && (
            <div className="form-grid">
              <FormField label="Ünvan" required>
                <input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="İlgili Kişi">
                <input value={form.contact_person || ''} onChange={e => setForm({ ...form, contact_person: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="Telefon">
                <input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="Mobil">
                <input value={form.mobile || ''} onChange={e => setForm({ ...form, mobile: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="E-posta">
                <input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="Şehir">
                <input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="Kaynak" hint="Web, fuar, referans, soğuk arama, WhatsApp...">
                <input value={form.source || ''} onChange={e => setForm({ ...form, source: e.target.value })} disabled={!isEditing} />
              </FormField>
              <FormField label="Aşama">
                <select value={form.status || 'open'} onChange={e => setForm({ ...form, status: e.target.value })} disabled={!isEditing}>
                  {COLUMNS.filter(c => c.key !== 'converted').map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Not" fullWidth>
                <textarea rows={3} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} disabled={!isEditing} />
              </FormField>
            </div>
          )}

          {selected && modalTab === 'activity' && (
            <div style={{ minHeight: 380 }}>
              <RecordChatter
                recordType="prospect_account"
                recordId={selected.id}
                currentUserId={user?.id}
              />
            </div>
          )}
        </Modal>
      )}

      {/* LOST REASON MODAL */}
      {lostModal && (
        <Modal
          title="Kaybedilme Nedeni"
          size="sm"
          onClose={() => setLostModal(null)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setLostModal(null)}>Vazgeç</Button>
              <Button variant="danger" onClick={confirmLost}>❌ Kaybedildi olarak işaretle</Button>
            </div>
          }
        >
          <div style={{ marginBottom: 12, padding: 10, background: 'var(--danger-bg)', borderRadius: 'var(--r-md)', border: '1px solid var(--danger)' }}>
            <strong style={{ color: 'var(--danger)' }}>{lostModal.prospectTitle}</strong>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 4 }}>
              Bu lead'i kaybedildi olarak işaretlemek üzeresiniz. Nedeni belirtirseniz, ileride <strong>kayıp analizi</strong> raporunda görünür.
            </div>
          </div>

          <div className="form-grid">
            <FormField label="Neden" fullWidth hint={lostReasons.length === 0 ? 'Henüz neden tanımlanmamış — Settings → Kaybedilme Nedenleri\'nden ekleyebilirsiniz.' : null}>
              <select
                value={lostForm.lost_reason_id}
                onChange={e => setLostForm({ ...lostForm, lost_reason_id: e.target.value })}
                disabled={lostReasons.length === 0}
              >
                <option value="">{lostReasons.length ? '— Seçiniz —' : '(Tanımlı neden yok)'}</option>
                {lostReasons.map(r => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Ek Not (opsiyonel)" fullWidth>
              <textarea
                rows={3}
                value={lostForm.lost_note}
                onChange={e => setLostForm({ ...lostForm, lost_note: e.target.value })}
                placeholder="örn. Rakip 30% daha ucuz teklif verdi"
              />
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}
